import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FirmService, Region } from '@6grain/masofa-monolith-api-client/dictionaries';
import { RegionManagerService } from '@components/openstreetmap/managers/region-manager.service';
import { FieldService } from '@6grain/masofa-monolith-api-client/crop-monitoring';
import { ProfileInfoViewModel, RoleService, UserService } from '@6grain/masofa-monolith-api-client/identity';
import { userNames } from '@app/utils/_msf';

export interface RecipientOption {
  label: string;
  value: string;
}

@Injectable()
export class NotificationRecipientCatalog {
  readonly users = signal<RecipientOption[]>([]);
  readonly roles = signal<RecipientOption[]>([]);
  readonly regions = signal<Region[]>([]);
  readonly firms = inject(FirmService);
  readonly fields = inject(FieldService);

  constructor() {
    const destroyRef = inject(DestroyRef);
    const usersApi = inject(UserService);
    const rolesApi = inject(RoleService);
    const regionManager = inject(RegionManagerService);

    regionManager.getRegionsObservable().pipe(takeUntilDestroyed(destroyRef)).subscribe({
      next: (data) => {
        this.regions.set(Array.isArray(data) ? data : data ? [data] : []);
      },
    });

    usersApi.identityUserCustomGetListPost({ take: 1000, offset: 0, filters: [] }).pipe(takeUntilDestroyed(destroyRef)).subscribe({
      next: (rows) => {
        this.users.set((rows ?? []).map((row) => {
          const wrapped = row as { baseInfo?: ProfileInfoViewModel };
          const item = wrapped.baseInfo ?? (row as ProfileInfoViewModel);
          const name = userNames(item).trim();
          const email = item.email?.trim() ?? '';
          const label = name && email && name !== email ? `${name} (${email})` : (name || email || item.id || '');
          return { label, value: item.id ?? '' };
        }).filter((option) => option.value && option.label));
      },
    });

    rolesApi.identityRoleGetRolesGet().pipe(takeUntilDestroyed(destroyRef)).subscribe({
      next: (rows) => {
        this.roles.set((rows ?? []).map((row) => ({
          label: row.name?.trim() || row.id || '',
          value: row.name?.trim() || '',
        })).filter((option) => option.value));
      },
    });
  }
}
